import { FeatureDomain } from '@akin-home/amplify';
import { colors, fonts } from '@akin-home/theme';
import { isRobotUser, useResponsiveStyling } from '@akin-home/utils/native';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  BottomTabNavigationOptions,
  createBottomTabNavigator,
} from '@react-navigation/bottom-tabs';
import { useMemo } from 'react';
import { Platform, useWindowDimensions } from 'react-native';
import AppSelectionScreen from '../../app_library/components/AppSelectionScreen';
import { CalendarScreenVideoWrapper } from '../../calendar/components/CalendarScreen/CalendarScreenVideoWrapper';
import ChecklistsScreen from '../../checklists/components/ChecklistsScreen';
import { drawerIcon } from '../../components/DrawerIcon/DrawerIcon';
import ContactsScreen from '../../contacts/components/ContactsScreen';
import { DashboardScreen } from '../../dashboard/DashboardScreen';
import YourGoalsScreen from '../../goals/components/YourGoalsScreen';
import { postPageViewLogEvent } from '../../logging';
import RobotsScreen from '../../robots/RobotsScreen';
import MealsScreen from '../../screens/MealsScreen';
import { color } from '../../theme';
import { getCurrentUserID } from '../../users/functions';

export type DrawerScreens = {
  /** Home dashboard screen */
  Home: undefined;
  // These route params determine which views to be shown. Moved this from being internal state of the component
  // to the route to allow recovering nav state from local storage and to more easily pass the context to components
  // in the CalendarAddItem form, this is preferable to passing props down a deeply nested tree structure or using
  // callbacks to change the state of CalendarScreen
  Calendar: undefined;

  Tasks: undefined;

  /** Screen for the meal planner */
  Meals: undefined;

  /** Goals screen */
  Goals: undefined;

  /** Contacts screen */
  Contacts?: {
    contact_id?: string;
  };

  /** Robots Screen */
  Robots: undefined;

  /** Apps screen */
  Apps: undefined;
};

const Tabs = createBottomTabNavigator<DrawerScreens>();

/** Focus event for logging page views */
const onFocusEvent = (page: string, feature_domain?: FeatureDomain) => () => {
  const user_id = getCurrentUserID();

  user_id &&
    postPageViewLogEvent({
      user_id,
      feature_domain,
      data: {
        page,
      },
    });
};

export const DrawerNavigator = () => {
  const { width, height } = useWindowDimensions();
  const isSmallScreen = Math.min(width, height) < 600;

  const { ms } = useResponsiveStyling();
  const options = useMemo(() => {
    const fontSize = ms(20, 0.35, { min: 15 });

    return getOptions(fontSize);
  }, [ms]);

  return (
    <Tabs.Navigator screenOptions={screenOptions} initialRouteName="Home">
      <Tabs.Screen
        name="Home"
        component={DashboardScreen}
        options={options.home}
        listeners={({ route }) => ({
          focus: onFocusEvent(route.name),
        })}
      />

      <Tabs.Screen
        name="Calendar"
        component={CalendarScreenVideoWrapper}
        options={options.calendar}
        listeners={({ route }) => ({
          focus: onFocusEvent(route.name, FeatureDomain.CALENDAR),
        })}
      />

      {!isSmallScreen ? (
        <Tabs.Screen
          name="Contacts"
          component={ContactsScreen}
          options={options.contacts}
          listeners={({ route }) => ({
            focus: onFocusEvent(route.name, FeatureDomain.CONTACTS),
          })}
        />
      ) : null}

      <Tabs.Screen
        name="Goals"
        component={YourGoalsScreen}
        options={options.goals}
        listeners={({ route }) => ({
          focus: onFocusEvent(route.name, FeatureDomain.GOALS),
        })}
      />

      <Tabs.Screen
        name="Tasks"
        component={ChecklistsScreen}
        options={options.tasks}
        listeners={({ route }) => ({
          focus: onFocusEvent(route.name, FeatureDomain.CHECKLISTS),
        })}
      />

      {!isSmallScreen ? (
        <Tabs.Screen
          name="Meals"
          component={MealsScreen}
          options={options.meals}
          listeners={({ route }) => ({
            focus: onFocusEvent(route.name, FeatureDomain.MEALS),
          })}
        />
      ) : null}

      {isRobotUser() && !isSmallScreen ? (
        <Tabs.Screen
          name="Robots"
          component={RobotsScreen}
          options={options.robots}
          listeners={({ route }) => ({
            focus: onFocusEvent(route.name),
          })}
        />
      ) : null}

      {Platform.OS === 'android' && !isSmallScreen ? (
        <Tabs.Screen
          name="Apps"
          component={AppSelectionScreen}
          options={options.apps}
          listeners={({ route }) => ({
            focus: onFocusEvent(route.name, FeatureDomain.APPS),
          })}
        />
      ) : null}
    </Tabs.Navigator>
  );
};

const createScreenOptions = (params: { icon: IconProp; fontSize: number }) => ({
  tabBarIcon: drawerIcon(params.icon),
  tabBarLabelStyle: {
    fontFamily: fonts.semibold,
    fontSize: params.fontSize,
  },
});

const getOptions = (fontSize: number) => ({
  home: createScreenOptions({ icon: 'home', fontSize }),
  calendar: createScreenOptions({ icon: 'calendar-check', fontSize }),
  meals: createScreenOptions({ icon: 'salad', fontSize }),
  tasks: createScreenOptions({ icon: 'tasks', fontSize }),
  goals: createScreenOptions({ icon: 'trophy-alt', fontSize }),
  contacts: createScreenOptions({ icon: 'user', fontSize }),
  robots: createScreenOptions({ icon: 'robot', fontSize }),
  apps: createScreenOptions({ icon: 'grid', fontSize }),
});

const screenOptions: BottomTabNavigationOptions = {
  headerShown: false,
  tabBarHideOnKeyboard: true,
  tabBarActiveBackgroundColor: 'rgba(47, 57, 238, 0.1)',
  tabBarStyle: {
    height: 64,

    /** At some point we should add this -80 margin.
     * We would need to create a wrapper that goes around each screen's content to offset from this. */
    // marginTop: -80,

    paddingHorizontal: 10,
    paddingVertical: 8,

    borderTopRightRadius: 20,
    borderTopLeftRadius: 20,
    borderWidth: 0,
    borderColor: color.border,
  },
  tabBarItemStyle: {
    borderRadius: 50,
    marginBottom: 8,
    height: 48,
  },
  tabBarActiveTintColor: colors.primary[900],
  tabBarLabelStyle: {
    fontFamily: fonts.semibold,
    fontSize: 20,
  },
  tabBarIconStyle: {
    // Is this required?
    // marginLeft: 8,
  },
};
